import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import {
  useQyAttachFileVisit,
  useQyCreateVisit,
} from "@factory/query/visit.query";
import { VisitFormRule, VisitFormSchema } from "@factory/form/form.rule";
import { visitFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useEffect } from "react";
import { GetVisitModel } from "@factory/model/query-model-visit";

export function useNewVisit() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapCreateVisit } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();
  let [searchParams, setSearchParams] = useSearchParams();

  const defaultPatientCode = searchParams.get("patient");
  const defaultPatientName = searchParams.get("name");
  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Visit is created`);
    navigate(`../`);
  };
  const { mutateAsync: addVisit, isLoading: isCreating } = useQyCreateVisit();
  const { mutateAsync: uploadAttachments } = useQyAttachFileVisit();

  // LOCAL FUNCTIONS
  useEffect(() => {
    if (defaultPatientCode) {
      setValue("patient", defaultPatientCode);
    }
  }, [defaultPatientCode]);

  useEffect(() => {
    if (defaultPatientName) {
      setValue("patientName", defaultPatientName);
    }
  }, [defaultPatientName]);

  const formMethod = useForm<VisitFormSchema>({
    defaultValues: visitFormDefault,
    resolver: zodResolver(VisitFormRule),
  });
  const { handleSubmit, setValue } = formMethod;

  const handleValidate = async (form: VisitFormSchema) => {
    const formData = mapCreateVisit(form);
    const visitResponse = await addVisit(formData);
    handleApiSuccess(visitResponse);

    const data = visitResponse.data as GetVisitModel;
    if (form.photos && data.code && (form.photos as FileList).length > 0) {
      await uploadAttachments({
        attachment: {
          visit: data.code,
        },
        file: [...(form.photos || [])],
      });
    }
  };
  const handleValidateError = (err: FieldErrors<VisitFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
